import { useState } from "react";
import { AlertTriangle, Mail, Plus, Users, X } from "lucide-react";
import { V } from "@/lib/vocab";

const MODES = [
  ["default", "Default"],
  ["also", "Also notify"],
  ["only", "Only these people"],
];

// Owning an obligation and being told about it are different questions: a board member can't
// file a renewal, but the treasurer should still know the insurance is coming due. So the owner
// dropdown stays managers-only while this picker offers the whole team — plus outside addresses
// for a broker or inspector who has no login at all.
export function NotifyPicker({ value, onChange, people, ownerUserId, ownerName, maxEmails = 10, disabled }) {
  const [draft, setDraft] = useState("");
  const mode = value.notify_mode || "default";
  const ids = value.notify_user_ids || [];
  const emails = value.notify_emails || [];
  const picking = mode !== "default";

  const hint = {
    default: `Whoever owns it — or the whole management team when nobody does.`,
    also: `The owner or team, plus everyone you pick below.`,
    only: `Just the people you pick below. Nobody else is reminded.`,
  }[mode];

  const toggle = (uid) => onChange({ notify_user_ids: ids.includes(uid) ? ids.filter((x) => x !== uid) : [...ids, uid] });
  const addEmail = () => {
    const addr = draft.trim().toLowerCase();
    if (!addr) return;
    if (!/^[^@\s]+@[^@\s.]+\.[^@\s]{2,}$/.test(addr)) return;
    if (!emails.includes(addr) && emails.length < maxEmails) onChange({ notify_emails: [...emails, addr] });
    setDraft("");
  };

  // The one way "only" can bite: silencing the person who's actually accountable.
  const ownerLeftOut = mode === "only" && ownerUserId && !ids.includes(ownerUserId);
  const nothingPicked = picking && !ids.length && !emails.length;

  return (
    <div data-testid="renewal-notify-block" className="rounded-xl border border-sand-200 bg-sand-50/60 p-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <Users className="h-4 w-4 text-ink/40 shrink-0" strokeWidth={1.75} />
        <span className="text-xs uppercase tracking-wider font-bold text-ink/50">Who gets the reminders</span>
      </div>
      <div className="flex flex-wrap gap-1.5" role="radiogroup">
        {MODES.map(([key, label]) => (
          <button key={key} type="button" disabled={disabled} data-testid={`renewal-notify-mode-${key}`}
            aria-checked={mode === key} role="radio" onClick={() => onChange({ notify_mode: key })}
            className={`rounded-full border px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${mode === key ? "border-ink bg-ink text-white" : "border-sand-300 bg-white text-ink hover:border-ink"}`}>
            {label}
          </button>
        ))}
      </div>
      <p className="text-xs text-ink/70" data-testid="renewal-notify-hint">{hint}</p>

      {picking && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="renewal-notify-people">
            {(people || []).map((p) => (
              <label key={p.user_id} data-testid={`renewal-notify-person-${p.user_id}`}
                className="flex items-center gap-2 rounded-lg border border-sand-200 bg-white px-2.5 py-2 cursor-pointer hover:border-ink/40 min-w-0">
                <input type="checkbox" disabled={disabled} checked={ids.includes(p.user_id)} onChange={() => toggle(p.user_id)}
                  className="h-3.5 w-3.5 accent-redline-500 shrink-0" />
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-ink truncate">{p.name}</span>
                  <span className="block text-[11px] text-ink/50 truncate">{V.role(p.role)}{p.user_id === ownerUserId ? " · owns this" : ""}</span>
                </span>
              </label>
            ))}
          </div>

          <div className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <Mail className="h-3.5 w-3.5 text-ink/40 shrink-0" />
              <span className="text-[11px] uppercase tracking-wider font-bold text-ink/50">Outside addresses</span>
              <span className="text-[11px] text-ink/50">no login needed — they get the email only</span>
            </div>
            {emails.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="renewal-notify-emails">
                {emails.map((e) => (
                  <span key={e} data-testid={`renewal-notify-email-${e}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-sand-300 bg-white px-2.5 py-1 text-xs font-medium text-ink">
                    {e}
                    {!disabled && (
                      <button type="button" aria-label={`Remove ${e}`} onClick={() => onChange({ notify_emails: emails.filter((x) => x !== e) })}
                        className="text-ink/40 hover:text-ink"><X className="h-3 w-3" /></button>
                    )}
                  </span>
                ))}
              </div>
            )}
            {!disabled && emails.length < maxEmails && (
              <div className="flex flex-col sm:flex-row gap-1.5">
                <input data-testid="renewal-notify-email-input" type="email" value={draft} placeholder="broker@agency.com"
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                  className="flex-1 min-w-0 rounded-lg border border-sand-200 bg-white px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-redline-500/30" />
                <button type="button" data-testid="renewal-notify-email-add" onClick={addEmail}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-sand-300 bg-white px-3 py-2 text-xs font-bold text-ink hover:border-ink shrink-0">
                  <Plus className="h-3.5 w-3.5" /> Add
                </button>
              </div>
            )}
          </div>

          {nothingPicked && (
            <p className="text-xs text-amber-800 flex items-start gap-1.5" data-testid="renewal-notify-empty-warn">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              Nobody picked yet — reminders keep following the {mode === "only" ? "owner or team" : "owner or team only"} until you choose someone.
            </p>
          )}
          {ownerLeftOut && (
            <p className="text-xs text-amber-800 flex items-start gap-1.5" data-testid="renewal-notify-owner-warn">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span><b>{ownerName || "The owner"}</b> owns this obligation but won't be reminded. Tick them too, or switch to “Also notify”.</span>
            </p>
          )}
        </>
      )}
    </div>
  );
}
